import { styled, keyframes } from '@mui/material/styles';
import { Box, Button, MenuItem, Select, Typography } from '@mui/material';

// 외부 진입(라커 번호 딥링크) 시 해당 카드를 강조하는 주황 펄스
const lockerHighlightPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(238, 115, 90, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(238, 115, 90, 0); }
  100% { box-shadow: 0 0 0 0 rgba(238, 115, 90, 0); }
`;

// ============================================
// Container & Layout
// ============================================
export const PageContainer = styled(Box)({
  minHeight: '700px',
  marginTop: '20px',
});

export const ControlSection = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  '@media print': {
    display: 'none', // 출력 시 상단 컨트롤 숨김
  },
});

export const ControlLeftSection = styled(Box)({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
});

export const SearchInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: '360px',
  height: '48px',
  padding: '12px 16px',
  border: '1px solid #b3b3b3',
  borderRadius: '4px',
  backgroundColor: '#fff',
});

export const SearchInput = styled('input')({
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  fontWeight: 500,
  color: '#b3b3b3',
  '&::placeholder': {
    color: '#b3b3b3',
  },
});

export const SearchIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#b3b3b3',
  cursor: 'pointer',
});

// sortDirection='vertical'일 때만 column-flow + 명시적 row 수로 column-major 배치
// 'horizontal'(기본)은 auto-fill 로 row-major 배치 유지
export const LockerGrid = styled(Box, {
  shouldForwardProp: prop => !['sortDirection', 'columns', 'rows'].includes(prop),
})(({ sortDirection, columns, rows }) => ({
  display: 'grid',
  gap: '16px',
  width: '100%',
  justifyContent: 'space-between',
  paddingBottom: '40px',
  ...(sortDirection === 'vertical' && columns > 0 && rows > 0
    ? {
        gridAutoFlow: 'column',
        gridTemplateColumns: `repeat(${columns}, 140px)`,
        gridTemplateRows: `repeat(${rows}, 126px)`,
      }
    : {
        gridTemplateColumns: 'repeat(auto-fill, 140px)',
      }),
  '@media print': {
    // 출력 시 8열 고정 + 전체 너비 강제 (지점별 콘텐츠 너비 차이 차단)
    // 화면에서 세로 정렬 중이어도 출력은 항상 가로(row-major)로 통일
    width: '100% !important',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gridTemplateRows: 'auto',
    gridAutoFlow: 'row',
    gap: '4px',
    justifyContent: 'initial',
    paddingBottom: 0,
  },
}));

export const FooterSection = styled(Box)({
  marginTop: '40px',
  display: 'flex',
  justifyContent: 'flex-end',
});

export const FooterText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
});

// ============================================
// Select & Button
// ============================================
export const LockerTypeSelect = styled(Select)({
  minWidth: '150px',
  fontSize: '16px',
  backgroundColor: '#fff',
  // && 로 specificity 높여서 MUI의 size="small" 기본 스타일을 확실히 덮어씀
  '&& .MuiSelect-select': {
    fontSize: '16px',
  },
  '&& .MuiInputBase-input': {
    fontSize: '16px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E0E0E0',
  },
});

export const AreaManageButton = styled(Button)({
  backgroundColor: '#1F3A5F',
  color: '#fff',
  borderRadius: '8px',
  paddingLeft: '20px',
  paddingRight: '20px',
  paddingTop: '8px',
  paddingBottom: '8px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#172C48',
  },
});

export const PrintButton = styled(Button)({
  backgroundColor: '#fff',
  color: '#1F3A5F',
  border: '1px solid #1F3A5F',
  borderRadius: '8px',
  paddingLeft: '20px',
  paddingRight: '20px',
  paddingTop: '8px',
  paddingBottom: '8px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(31, 58, 95, 0.08)',
    borderColor: '#172C48',
  },
});

// 가로/세로 정렬 토글 버튼 (PrintButton 과 동일한 outlined 톤)
export const SortToggleButton = styled(Button)({
  backgroundColor: '#fff',
  color: '#1F3A5F',
  border: '1px solid #1F3A5F',
  borderRadius: '8px',
  paddingLeft: '20px',
  paddingRight: '20px',
  paddingTop: '8px',
  paddingBottom: '8px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(31, 58, 95, 0.08)',
    borderColor: '#172C48',
  },
});

// ============================================
// Print-only Header (화면에는 안 보이고 출력 시에만 노출)
// 1페이지 상단에만 표시. 공간 절약을 위해 컴팩트하게 제작.
// ============================================
export const PrintOnlyHeader = styled(Box)({
  display: 'none',
  '@media print': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    marginBottom: '8px',
    paddingBottom: '6px',
    borderBottom: '1px solid #262626',
  },
});

export const PrintTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#262626',
});

export const PrintSubtitle = styled(Typography)({
  fontSize: '10px',
  color: '#555',
});

export const LockerMenuItem = styled(MenuItem)({
  fontSize: '16px',
});

// ============================================
// Print Page (출력 시 72개 단위 강제 페이지 분리 래퍼)
// 화면에는 영향 없음. 출력 시에만 page-break 발동 + 너비 고정.
// ============================================
export const LockerPage = styled(Box, {
  shouldForwardProp: prop => prop !== 'isLastPage',
})(({ isLastPage }) => ({
  '@media print': {
    width: '100% !important',
    pageBreakAfter: isLastPage ? 'auto' : 'always',
    breakAfter: isLastPage ? 'auto' : 'page',
  },
}));

// ============================================
// Locker Card
// ============================================
export const LockerCard = styled(Box, {
  shouldForwardProp: prop => !['cardBgColor', 'cardBorder', 'highlighted'].includes(prop),
})(({ cardBgColor, cardBorder, highlighted }) => ({
  height: '126px',
  width: '140px',
  backgroundColor: cardBgColor || '#FFFFFF',
  border: cardBorder || 'none',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: '10px',
  cursor: 'pointer',
  boxShadow: '0px 2px 8px rgba(0,0,0,0.16)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
  },
  ...(highlighted && {
    outline: '2px solid #EE735A',
    outlineOffset: '2px',
    animation: `${lockerHighlightPulse} 1.6s ease-out infinite`,
  }),
  '@media print': {
    // 출력 전용 사이즈 — A4 1페이지 = 8열 × 9행 = 72개 고정
    // height 100px: 1페이지(헤더 40px + 9행×100px + 8×4px gap ≈ 972px)가
    // A4 여유 공간(약 1085px)에 안전하게 들어가도록 계산된 값.
    // 브라우저 기본 머리글/바닥글이 켜져 있어도 버퍼 있음.
    width: '100% !important', // 그리드 셀을 가득 채워서 콘텐츠 따라 너비 변화 방지
    height: '100px',
    boxSizing: 'border-box',
    gap: '2px',
    padding: '4px',
    boxShadow: 'none',
    border: cardBorder || '1px solid #ccc',
    pageBreakInside: 'avoid',
  },
}));

export const LockerCardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '120px',
  height: '28px',
  '@media print': {
    width: '100%',
    height: 'auto',
  },
});

export const LockerNumber = styled(Typography, {
  shouldForwardProp: prop => prop !== 'textColor',
})(({ textColor }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: textColor || '#262626',
  letterSpacing: '-0.2px',
  '@media print': { fontSize: '14px' },
}));

export const MemberName = styled(Typography, {
  shouldForwardProp: prop => prop !== 'textColor',
})(({ textColor }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: textColor || '#262626',
  maxWidth: '70px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  '@media print': {
    fontSize: '12px',
    maxWidth: '55px',
  },
}));

export const LockerCardBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '120px',
  height: '34px',
  '@media print': {
    width: '100%',
    height: 'auto',
  },
});

export const DateText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'textColor',
})(({ textColor }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: textColor || '#262626',
  lineHeight: 1.2,
  textAlign: 'center',
  '@media print': { fontSize: '10px' },
}));

export const EmptyDateText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'textColor',
})(({ textColor }) => ({
  fontSize: '13px',
  fontWeight: 500,
  color: textColor || '#262626',
  textAlign: 'center',
  '@media print': { fontSize: '11px' },
}));

export const OverdueDateText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'textColor',
})(({ textColor }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: textColor || '#FFF',
  lineHeight: 1.2,
  textAlign: 'center',
  '@media print': { fontSize: '11px' },
}));

export const OverdueDaysText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'textColor',
})(({ textColor }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: textColor || '#FFF',
  lineHeight: 1.2,
  textAlign: 'center',
  '@media print': { fontSize: '11px' },
}));

export const LockerCardFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '100%',
});

// ============================================
// Status Badge
// ============================================
export const StatusBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'statusColor' && prop !== 'statusBorder',
})(({ statusBorder }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 12px',
  borderRadius: '999px',
  backgroundColor: 'transparent',
  border: statusBorder,
  '@media print': { padding: '2px 7px' },
}));

export const StatusText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'statusColor',
})(({ statusColor }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: statusColor,
  whiteSpace: 'nowrap',
  textAlign: 'center',
  '@media print': { fontSize: '9px' },
}));

// ============================================
// Action Buttons (Add/Delete)
// ============================================
export const DeleteLockerButton = styled(Box)({
  height: '110px',
  width: '120px',
  border: '2px dashed #FF5252',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: 'rgba(255, 82, 82, 0.05)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 82, 82, 0.1)',
    borderColor: '#D32F2F',
  },
});

export const AddLockerButton = styled(Box)({
  height: '110px',
  width: '120px',
  border: '2px dashed #1F3A5F',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: 'rgba(31, 58, 95, 0.05)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(31, 58, 95, 0.1)',
    borderColor: '#172C48',
  },
});

export const AddLockerButtonWithMargin = styled(AddLockerButton)({
  marginBottom: '20px',
});

export const AddLockerText = styled(Typography)({
  fontSize: '12px',
  lineHeight: '20px',
  fontWeight: 600,
  textAlign: 'center',
  color: '#1F3A5F',
  marginBottom: '4px',
});

export const ActionIcon = styled('img')({
  width: '32px',
  height: '32px',
});

export const SmallActionIcon = styled('img')({
  width: '24px',
  height: '24px',
});
